import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import {
  ExternalLink,
  Loader2,
  RefreshCw,
  Search,
  List,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Check,
  Users,
  UserCheck,
  Target,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { formatDate } from "@/lib/utils";

type Contact = {
  id: string;
  email: string | null;
  firstname: string | null;
  lastname: string | null;
  company: string | null;
  lifecyclestage: string | null;
  addedToListAt: string | null;
};

type HubspotList = {
  listId: string;
  name: string;
  processingType: string;
  size: number;
};

type SelectedList = { listId: string; listName: string };

function ListPicker({
  onSelect,
  currentList,
}: {
  onSelect: (list: HubspotList) => void;
  currentList: SelectedList | null;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [lists, setLists] = useState<HubspotList[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const fetchLists = useCallback(async (q?: string) => {
    setLoading(true);
    setError(null);
    try {
      const url = q
        ? `/api/hubspot/lists?q=${encodeURIComponent(q)}`
        : "/api/hubspot/lists";
      const res = await fetch(url, { credentials: "include" });
      const data = await res.json();
      if (data.error) setError(data.error);
      setLists(data.lists ?? []);
    } catch {
      setError("Failed to load lists");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && lists.length === 0 && !loading) fetchLists();
  }, [open, lists.length, loading, fetchLists]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchLists(search || undefined);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [search, open, fetchLists]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-md border bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
      >
        <List className="h-3 w-3" />
        {currentList ? currentList.listName : "Select List"}
        <ChevronDown className="h-3 w-3" />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-80 rounded-lg border bg-white shadow-lg">
          <div className="border-b p-2">
            <div className="flex items-center gap-2 rounded-md border bg-slate-50 px-2.5 py-1.5">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search lists..."
                className="w-full bg-transparent text-xs outline-none placeholder:text-muted-foreground"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}

            {error && (
              <div className="px-3 py-2 text-xs text-destructive">{error}</div>
            )}

            {!loading && lists.length === 0 && !error && (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                No lists found
              </div>
            )}

            {!loading &&
              lists.map((list) => {
                const isSelected = currentList?.listId === list.listId;
                return (
                  <button
                    key={list.listId}
                    type="button"
                    onClick={() => {
                      onSelect(list);
                      setOpen(false);
                    }}
                    className="flex w-full items-center gap-3 px-3 py-2 text-left text-xs hover:bg-slate-50"
                  >
                    <div className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
                      {isSelected && (
                        <Check className="h-3.5 w-3.5 text-primary" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{list.name}</div>
                      <div className="text-muted-foreground">
                        {(list.size ?? 0).toLocaleString()} contacts &middot;{" "}
                        {(list.processingType ?? "manual").toLowerCase()}
                      </div>
                    </div>
                  </button>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

function ListSelectionPrompt({
  onSelect,
}: {
  onSelect: (list: HubspotList) => void;
}) {
  const [search, setSearch] = useState("");
  const [lists, setLists] = useState<HubspotList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const fetchLists = useCallback(async (q?: string) => {
    setLoading(true);
    setError(null);
    try {
      const url = q
        ? `/api/hubspot/lists?q=${encodeURIComponent(q)}`
        : "/api/hubspot/lists";
      const res = await fetch(url, { credentials: "include" });
      const data = await res.json();
      if (data.error) setError(data.error);
      setLists(data.lists ?? []);
    } catch {
      setError("Failed to load lists");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchLists(search || undefined);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [search, fetchLists]);

  return (
    <div className="rounded-xl border bg-card">
      <div className="border-b px-6 py-5">
        <h2 className="text-base font-semibold">Select a Marketing List</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Choose a HubSpot marketing list to use as your waitlist.
        </p>
      </div>

      <div className="border-b px-6 py-3">
        <div className="flex items-center gap-2 rounded-md border bg-slate-50 px-3 py-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search marketing lists..."
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <div className="px-6 py-3 text-sm text-destructive">{error}</div>
        )}

        {!loading && lists.length === 0 && !error && (
          <div className="px-6 py-12 text-center text-sm text-muted-foreground">
            No marketing lists found in your HubSpot account.
          </div>
        )}

        {!loading &&
          lists.map((list) => (
            <button
              key={list.listId}
              type="button"
              onClick={() => onSelect(list)}
              className="flex w-full items-center gap-4 border-b px-6 py-3 text-left transition-colors last:border-b-0 hover:bg-slate-50/60"
            >
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100">
                <List className="h-4 w-4 text-slate-500" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{list.name}</div>
                <div className="text-xs text-muted-foreground">
                  {(list.size ?? 0).toLocaleString()} contacts &middot;{" "}
                  {(list.processingType ?? "manual").toLowerCase()} list
                </div>
              </div>
              <ChevronDown className="h-4 w-4 -rotate-90 text-muted-foreground" />
            </button>
          ))}
      </div>
    </div>
  );
}

const PROSPECT_STAGES = new Set([
  "marketingqualifiedlead",
  "salesqualifiedlead",
]);

function computeStats(contacts: Contact[]) {
  const total = contacts.length;
  let customers = 0;
  let prospects = 0;

  for (const c of contacts) {
    const stage = c.lifecyclestage?.toLowerCase() ?? "";
    if (stage === "customer") customers++;
    else if (PROSPECT_STAGES.has(stage)) prospects++;
  }
  return { total, customers, prospects };
}

function computeGrowthData(contacts: Contact[]) {
  const sorted = contacts
    .filter((c) => c.addedToListAt)
    .map((c) => ({ ...c, _ts: new Date(c.addedToListAt!).getTime() }))
    .sort((a, b) => a._ts - b._ts);

  if (sorted.length === 0) return [];

  const buckets = new Map<string, number>();
  for (const c of sorted) {
    const day = new Date(c._ts).toISOString().slice(0, 10);
    buckets.set(day, (buckets.get(day) ?? 0) + 1);
  }

  const entries = Array.from(buckets.entries()).sort(
    ([a], [b]) => a.localeCompare(b)
  );

  let cumulative = 0;
  return entries.map(([date, count]) => {
    cumulative += count;
    return { date, added: count, total: cumulative };
  });
}

function StatCard({
  label,
  value,
  icon: Icon,
  accent,
}: {
  label: string;
  value: number;
  icon: React.ElementType;
  accent: string;
}) {
  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex items-center gap-3">
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <div className="text-2xl font-semibold tracking-tight">
            {value.toLocaleString()}
          </div>
          <div className="text-xs font-medium text-muted-foreground">{label}</div>
        </div>
      </div>
    </div>
  );
}

function GrowthChart({ contacts }: { contacts: Contact[] }) {
  const data = useMemo(() => computeGrowthData(contacts), [contacts]);

  if (data.length < 2) return null;

  return (
    <div className="rounded-xl border bg-card p-5">
      <h3 className="mb-4 text-sm font-semibold">Waitlist Growth</h3>
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="waitlistGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6366f1" stopOpacity={0.2} />
              <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11 }}
            tickFormatter={(v: string) => {
              const d = new Date(v + "T00:00:00");
              return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
            }}
            stroke="#94a3b8"
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            stroke="#94a3b8"
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              fontSize: 12,
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
            labelFormatter={(v) => {
              const d = new Date(String(v) + "T00:00:00");
              return d.toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              });
            }}
          />
          <Area
            type="monotone"
            dataKey="total"
            name="Total"
            stroke="#6366f1"
            strokeWidth={2}
            fill="url(#waitlistGrad)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function WaitlistStats({ contacts }: { contacts: Contact[] }) {
  const { total, customers, prospects } = useMemo(
    () => computeStats(contacts),
    [contacts]
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <StatCard
          label="Total Waitlist"
          value={total}
          icon={Users}
          accent="bg-indigo-100 text-indigo-600"
        />
        <StatCard
          label="Prospects"
          value={prospects}
          icon={Target}
          accent="bg-amber-100 text-amber-600"
        />
        <StatCard
          label="Customers"
          value={customers}
          icon={UserCheck}
          accent="bg-emerald-100 text-emerald-600"
        />
      </div>
      <GrowthChart contacts={contacts} />
    </div>
  );
}

export function WaitlistTab() {
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [needsListSelection, setNeedsListSelection] = useState(false);
  const [selectedList, setSelectedList] = useState<SelectedList | null>(null);
  const [tableSearch, setTableSearch] = useState("");
  const [page, setPage] = useState(0);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/hubspot/waitlist", {
        credentials: "include",
      });
      const data = await res.json();
      setConnected(data.connected);
      setContacts(data.contacts ?? []);
      setNeedsListSelection(data.needsListSelection ?? false);
      setSelectedList(data.selectedList ?? null);
      if (data.error) setError(data.error);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const PAGE_SIZE = 25;

  const filteredContacts = useMemo(() => {
    const q = tableSearch.toLowerCase().trim();
    const filtered = q
      ? contacts.filter((c) => {
          const name = [c.firstname, c.lastname].filter(Boolean).join(" ");
          return (
            name.toLowerCase().includes(q) ||
            (c.email?.toLowerCase().includes(q) ?? false) ||
            (c.company?.toLowerCase().includes(q) ?? false)
          );
        })
      : contacts;

    return [...filtered].sort((a, b) => {
      const ta = a.addedToListAt ? new Date(a.addedToListAt).getTime() : 0;
      const tb = b.addedToListAt ? new Date(b.addedToListAt).getTime() : 0;
      return tb - ta;
    });
  }, [contacts, tableSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredContacts.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageContacts = filteredContacts.slice(
    safePage * PAGE_SIZE,
    safePage * PAGE_SIZE + PAGE_SIZE
  );

  useEffect(() => {
    setPage(0);
  }, [tableSearch]);

  async function handleSelectList(list: HubspotList) {
    try {
      const res = await fetch("/api/hubspot/select-list", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ listId: list.listId, listName: list.name }),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error ?? "Failed to select list");
        return;
      }
      setSelectedList({ listId: list.listId, listName: list.name });
      setNeedsListSelection(false);
      load();
    } catch {
      setError("Failed to select list");
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!connected) {
    return (
      <div className="rounded-xl border border-dashed bg-card p-12 text-center">
        <h3 className="text-base font-semibold">Connect HubSpot</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Pull a waitlist of prospective customers from a HubSpot marketing
          list.
        </p>
        <a
          href="/api/hubspot/auth"
          className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#ff7a59] px-4 py-2 text-sm font-medium text-white hover:bg-[#e96a4c]"
        >
          Connect HubSpot
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    );
  }

  if (needsListSelection) {
    return <ListSelectionPrompt onSelect={handleSelectList} />;
  }

  return (
    <div className="space-y-4">
      {contacts.length > 0 && <WaitlistStats contacts={contacts} />}

      <div className="rounded-xl border bg-card">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-base font-semibold">Waitlist</h2>
            <p className="text-xs text-muted-foreground">
              {selectedList
                ? `Showing contacts from "${selectedList.listName}"`
                : "Prospective customers from your HubSpot marketing list"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ListPicker
              onSelect={handleSelectList}
              currentList={selectedList}
            />
            <button
              type="button"
              onClick={load}
              className="flex items-center gap-1.5 rounded-md border bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
            >
              <RefreshCw className="h-3 w-3" />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="border-b bg-destructive/5 px-6 py-3 text-xs text-destructive">
            {error}
          </div>
        )}

        {contacts.length > 0 && (
          <div className="border-b px-6 py-3">
            <div className="flex items-center gap-2 rounded-md border bg-slate-50 px-3 py-1.5">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                placeholder="Search by name, email, or company..."
                className="w-full bg-transparent text-xs outline-none placeholder:text-muted-foreground"
              />
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b text-left">
                <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Name
                </th>
                <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Email
                </th>
                <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Company
                </th>
                <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Status
                </th>
                <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Added
                </th>
              </tr>
            </thead>
            <tbody>
              {pageContacts.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-12 text-center text-sm text-muted-foreground"
                  >
                    {tableSearch
                      ? "No contacts match your search."
                      : "No contacts found in the selected list."}
                  </td>
                </tr>
              ) : (
                pageContacts.map((c) => (
                  <tr
                    key={c.id}
                    className="border-b last:border-b-0 hover:bg-slate-50/60"
                  >
                    <td className="px-6 py-3 text-sm font-medium">
                      {[c.firstname, c.lastname].filter(Boolean).join(" ") ||
                        "—"}
                    </td>
                    <td className="px-6 py-3 text-sm">{c.email ?? "—"}</td>
                    <td className="px-6 py-3 text-sm">{c.company ?? "—"}</td>
                    <td className="px-6 py-3 text-sm">
                      {c.lifecyclestage ? (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">
                          {c.lifecyclestage}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-6 py-3 text-sm text-muted-foreground">
                      {c.addedToListAt ? formatDate(c.addedToListAt) : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filteredContacts.length > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t px-6 py-3">
            <span className="text-xs text-muted-foreground">
              {safePage * PAGE_SIZE + 1}–
              {Math.min((safePage + 1) * PAGE_SIZE, filteredContacts.length)} of{" "}
              {filteredContacts.length.toLocaleString()} contacts
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={safePage === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="rounded-md border bg-white p-1.5 text-xs font-medium hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <span className="px-2 text-xs font-medium">
                {safePage + 1} / {totalPages}
              </span>
              <button
                type="button"
                disabled={safePage >= totalPages - 1}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                className="rounded-md border bg-white p-1.5 text-xs font-medium hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
